import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

/**
 * Componente input OTP riutilizzabile
 * Accetta solo numeri e valida la lunghezza specificata
 *
 * Uso:
 * <app-otp-input
 *   [length]="6"
 *   [label]="'Inserisci il codice'"
 *   (valueChange)="onOtpChange($event)"
 *   (isValidChange)="onValidityChange($event)"
 * ></app-otp-input>
 */
@Component({
  selector: 'pt-otp-input',
  template: `
    <div class="otp-container">
      <label *ngIf="label" [for]="inputId" class="otp-label">
        {{ label }}
      </label>

      <div class="otp-input-wrapper">
        <input
          #otpInput
          [id]="inputId"
          type="text"
          class="otp-input"
          [class.is-invalid]="isInvalid && otpControl.value"
          [placeholder]="placeholder"
          [maxlength]="length"
          [formControl]="otpControl"
          autocomplete="off"
          inputmode="numeric"
        />

        <div *ngIf="isInvalid && otpControl.value" class="otp-error-message">
          {{ errorMessage }}
        </div>
      </div>

      <div *ngIf="showCharCount" class="otp-char-count">
        {{ otpControl.value?.length || 0 }} / {{ length }}
      </div>
    </div>
  `,
  styles: [
    `
      .otp-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .otp-label {
        display: block;
        font-weight: 600;
        font-size: 14px;
        color: #384048;
        letter-spacing: 0.3px;
      }

      .otp-input-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
      }

      .otp-input {
        font-size: 28px;
        font-weight: 700;
        letter-spacing: 10px;
        text-align: center;
        font-family: 'Courier New', monospace;
        max-width: 220px;
        padding: 14px 16px;
        border: 2px solid #e3e9f1;
        border-radius: 8px;
        background: #fff;
        color: #384048;
        transition: all 0.2s ease;
      }

      .otp-input::placeholder {
        letter-spacing: 8px;
        color: #aeb8c4;
      }

      .otp-input:focus {
        outline: none;
        border-color: #2f6fb2;
        box-shadow: 0 0 0 3px rgba(47, 111, 178, 0.1);
      }

      .otp-input.is-invalid {
        border-color: #dc3545;
        background-color: #fff5f5;
      }

      .otp-input.is-invalid:focus {
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
      }

      .otp-error-message {
        font-size: 13px;
        font-weight: 500;
        color: #dc3545;
        text-align: center;
        animation: slideDown 0.2s ease;
      }

      .otp-char-count {
        font-size: 12px;
        color: #677483;
        text-align: center;
        font-weight: 500;
      }

      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* ──── Responsive ──── */
      @media (max-width: 560px) {
        .otp-input {
          font-size: 24px;
          letter-spacing: 6px;
          max-width: 180px;
          padding: 12px 14px;
        }

        .otp-input::placeholder {
          letter-spacing: 4px;
        }
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpInputComponent),
      multi: true,
    },
  ],
})
export class OtpInputComponent implements ControlValueAccessor, OnInit {
  @ViewChild('otpInput') otpInput: ElementRef<HTMLInputElement>;

  /**
   * Lunghezza del codice OTP (default: 6)
   */
  @Input() length: number = 6;

  /**
   * Etichetta dell'input
   */
  @Input() label: string = '';

  /**
   * Placeholder dell'input
   */
  @Input() placeholder: string = '';

  /**
   * Messaggio di errore personalizzato
   */
  @Input() errorMessage: string = '';

  /**
   * Mostra il contatore di caratteri
   */
  @Input() showCharCount: boolean = false;

  /**
   * ID personalizzato per l'input (default: generato automaticamente)
   */
  @Input() inputId: string = `otp-input-${Math.random()
    .toString(36)
    .substr(2, 9)}`;

  /**
   * Emette il valore del codice OTP
   */
  @Output() valueChange = new EventEmitter<string>();

  /**
   * Emette true se il codice è valido (lunghezza corretta)
   */
  @Output() isValidChange = new EventEmitter<boolean>();

  otpControl = new FormControl('');
  isInvalid: boolean = false;

  private onTouched: () => void = () => {};
  private onChange: (value: string) => void = () => {};

  ngOnInit(): void {
    this.initializeErrorMessage();
    this.setupInputListener();
  }

  /**
   * Inizializza il messaggio di errore se non fornito
   */
  private initializeErrorMessage(): void {
    if (!this.errorMessage) {
      this.errorMessage = `Il codice deve contenere esattamente ${this.length} cifre`;
    }

    if (!this.placeholder) {
      this.placeholder = '0'.repeat(this.length);
    }
  }

  /**
   * Setup del listener per l'input
   * Filtra solo numeri e emette il valore
   */
  private setupInputListener(): void {
    this.otpControl.valueChanges.subscribe((value: string) => {
      // Filtra solo numeri
      const numericValue = (value || '')
        .replace(/[^0-9]/g, '')
        .slice(0, this.length);

      if (numericValue !== value) {
        this.otpControl.setValue(numericValue, { emitEvent: false });
      }

      // Emetti il valore
      this.valueChange.emit(numericValue);
      this.onChange(numericValue);

      // Valida e emetti lo stato di validità
      const isValid = numericValue.length === this.length;
      this.isInvalid = numericValue.length > 0 && !isValid;
      this.isValidChange.emit(isValid);
    });
  }

  /**
   * ControlValueAccessor: Scrivi il valore nel control
   */
  writeValue(value: string): void {
    if (value) {
      this.otpControl.setValue(value, { emitEvent: false });
    }
  }

  /**
   * ControlValueAccessor: Registra il callback onChange
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor: Registra il callback onTouched
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor: Imposta lo stato disabled
   */
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.otpControl.disable() : this.otpControl.enable();
  }

  /**
   * Metodo pubblico per resettare l'input
   */
  reset(): void {
    this.otpControl.reset();
    this.isInvalid = false;
  }

  /**
   * Metodo pubblico per ottenere il valore
   */
  getValue(): string {
    return this.otpControl.value || '';
  }

  /**
   * Metodo pubblico per verificare la validità
   */
  isValid(): boolean {
    return this.getValue().length === this.length;
  }

  /**
   * Metodo pubblico per impostare il focus
   */
  focus(): void {
    this.otpInput?.nativeElement?.focus();
  }
}
